import React, { useState, useMemo } from 'react';
import { useStore, Collection, Folder, CollectionRequest } from '../../stores';
import { FolderPlus, ChevronRight, ChevronDown, Folder as FolderIcon, MoreHorizontal, Plus, Clock, FolderOpen, Upload } from 'lucide-react';
import { HistoryList } from '../../features/history';
import { CollectionDialog, FolderDialog, CollectionContextMenu, FolderContextMenu, RequestContextMenu, ImportCollectionDialog, CollectionSettingsDialog } from '../../features/collections';
import { generateId } from '../../lib/utils';
import { PresenceDot, usePresence } from '../../features/presence';
import { downloadPostmanCollection, buildRequestDataMapFromTabs } from '../../lib/export';
import { useCollections } from '../../hooks/useCollections';

type SidebarTab = 'collections' | 'history';

export function Sidebar() {
  const {
    workspaces,
    activeWorkspaceId,
    deleteFolder,
    moveRequest,
    tabs,
  } = useStore();
  const {
    collections,
    deleteCollection: hookDeleteCollection,
    deleteRequest: hookDeleteRequest,
    createRequest: hookCreateRequest,
    fetchAndOpenRequest,
  } = useCollections();
  const { getUsersEditingRequest } = usePresence();

  // Filter collections by active workspace (with defensive array check)
  const filteredCollections = useMemo(() => {
    const safeCollections = Array.isArray(collections) ? collections : [];
    if (!activeWorkspaceId) {
      // Show all collections if no workspace selected
      return safeCollections;
    }
    return safeCollections.filter((c) => c.workspaceId === activeWorkspaceId);
  }, [collections, activeWorkspaceId]);
  const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<SidebarTab>('collections');

  // Dialog states
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null);
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [folderCollectionId, setFolderCollectionId] = useState<string>('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [settingsCollection, setSettingsCollection] = useState<Collection | null>(null);

  const toggleCollection = (id: string) => {
    setExpandedCollections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleFolder = (id: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleRequestClick = async (collectionId: string, request: CollectionRequest) => {
    await fetchAndOpenRequest(collectionId, request);
  };

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: 'text-blue-400',
      POST: 'text-green-400',
      PUT: 'text-orange-400',
      PATCH: 'text-cyan-400',
      DELETE: 'text-red-400',
      HEAD: 'text-purple-400',
      OPTIONS: 'text-blue-600',
    };
    return colors[method.toUpperCase()] || 'text-gray-400';
  };

  // Collection actions
  const handleAddFolder = (collectionId: string) => {
    setFolderCollectionId(collectionId);
    setEditingFolder(null);
    setFolderDialogOpen(true);
  };

  const handleEditCollection = (collection: Collection) => {
    setEditingCollection(collection);
    setCollectionDialogOpen(true);
  };

  const handleDeleteCollection = (collectionId: string) => {
    if (window.confirm('Are you sure you want to delete this collection? All requests will be lost.')) {
      hookDeleteCollection(collectionId);
    }
  };

  // Folder actions
  const handleEditFolder = (collectionId: string, folder: Folder) => {
    setFolderCollectionId(collectionId);
    setEditingFolder(folder);
    setFolderDialogOpen(true);
  };

  const handleDeleteFolder = (collectionId: string, folderId: string) => {
    if (window.confirm('Delete this folder? Requests will be moved to the collection root.')) {
      deleteFolder(collectionId, folderId, true);
    }
  };

  // Request actions
  const handleMoveRequest = (collectionId: string, requestId: string, targetFolderId: string | null, sourceFolderId: string | null) => {
    moveRequest(collectionId, requestId, targetFolderId, sourceFolderId);
  };

  const handleDeleteRequest = (collectionId: string, requestId: string, folderId: string | null) => {
    if (window.confirm('Are you sure you want to delete this request?')) {
      hookDeleteRequest(collectionId, requestId, folderId);
    }
  };

  const handleDuplicateRequest = (collectionId: string, request: CollectionRequest, folderId: string | null) => {
    hookCreateRequest(
      collectionId,
      {
        id: generateId(),
        name: `${request.name} (copy)`,
        method: request.method as any,
        url: '',
        headers: [],
        params: [],
      },
      folderId || undefined
    );
  };

  // Export collection to Postman format
  const handleExportCollection = (collection: Collection) => {
    // Build a map of request IDs to full request data from open tabs
    const requestDataMap = buildRequestDataMapFromTabs(tabs);
    downloadPostmanCollection(collection, requestDataMap);
  };

  // Render a single request item
  const renderRequest = (
    collectionId: string,
    request: CollectionRequest,
    folderId: string | null,
    folders: Folder[]
  ) => {
    const usersEditing = getUsersEditingRequest(request.id);

    return (
      <RequestContextMenu
        key={request.id}
        collectionId={collectionId}
        request={request}
        folders={folders}
        currentFolderId={folderId}
        onMoveToFolder={(targetFolderId) => handleMoveRequest(collectionId, request.id, targetFolderId, folderId)}
        onDeleteRequest={() => handleDeleteRequest(collectionId, request.id, folderId)}
        onDuplicateRequest={() => handleDuplicateRequest(collectionId, request, folderId)}
      >
        <div
          className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-accent cursor-pointer"
          onClick={() => handleRequestClick(collectionId, request)}
        >
          <span className={`text-xs font-mono font-medium ${getMethodColor(request.method)}`}>
            {request.method}
          </span>
          <span className="text-sm text-foreground truncate flex-1">
            {request.name}
          </span>
          {usersEditing.length > 0 && (
            <PresenceDot users={usersEditing} className="flex-shrink-0" />
          )}
        </div>
      </RequestContextMenu>
    );
  };

  // Collections content component
  const CollectionsContent = () => (
    <>
      {/* Collections Header */}
      <div className="h-8 flex items-center justify-between px-2 border-b border-border/50">
        <span className="text-xs font-medium text-foreground">Collections</span>
        <div className="flex items-center gap-0.5">
          <button
            className="p-1 rounded hover:bg-accent transition-colors"
            title="Import Collection"
            onClick={() => setImportDialogOpen(true)}
          >
            <Upload className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
          <button
            className="p-1 rounded hover:bg-accent transition-colors"
            title="New Collection"
            onClick={() => {
              setEditingCollection(null);
              setCollectionDialogOpen(true);
            }}
          >
            <FolderPlus className="w-3.5 h-3.5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Collections Tree */}
      <div className="flex-1 overflow-y-auto p-2">
        {filteredCollections.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground mb-2">No collections yet</p>
            <button
              className="text-sm text-primary hover:underline"
              onClick={() => {
                setEditingCollection(null);
                setCollectionDialogOpen(true);
              }}
            >
              Create a collection
            </button>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredCollections.map((collection) => (
              <div key={collection.id}>
                {/* Collection Item */}
                <CollectionContextMenu
                  collection={collection}
                  onAddFolder={() => handleAddFolder(collection.id)}
                  onEditCollection={() => handleEditCollection(collection)}
                  onDeleteCollection={() => handleDeleteCollection(collection.id)}
                  onExportCollection={() => handleExportCollection(collection)}
                  onOpenSettings={() => setSettingsCollection(collection)}
                >
                  <div
                    className="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-accent cursor-pointer group"
                    onClick={() => toggleCollection(collection.id)}
                  >
                    {expandedCollections.has(collection.id) ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    )}
                    <FolderIcon className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <span className="text-sm text-foreground truncate flex-1">
                      {collection.name}
                    </span>
                    <button
                      className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-background transition-all"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddFolder(collection.id);
                      }}
                      title="Add folder"
                    >
                      <Plus className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                </CollectionContextMenu>

                {/* Collection Contents */}
                {expandedCollections.has(collection.id) && (
                  <div className="ml-4 mt-1 space-y-0.5">
                    {/* Folders */}
                    {collection.folders?.map((folder) => (
                      <div key={folder.id}>
                        <FolderContextMenu
                          collectionId={collection.id}
                          folder={folder}
                          onEditFolder={() => handleEditFolder(collection.id, folder)}
                          onDeleteFolder={() => handleDeleteFolder(collection.id, folder.id)}
                        >
                          <div
                            className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-accent cursor-pointer group"
                            onClick={() => toggleFolder(folder.id)}
                          >
                            {expandedFolders.has(folder.id) ? (
                              <ChevronDown className="w-3 h-3 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="w-3 h-3 text-muted-foreground" />
                            )}
                            <FolderIcon className="w-3 h-3 text-yellow-500" />
                            <span className="text-sm text-foreground truncate flex-1">
                              {folder.name}
                            </span>
                            <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-100">
                              {folder.requests?.length || 0}
                            </span>
                          </div>
                        </FolderContextMenu>

                        {expandedFolders.has(folder.id) && folder.requests && (
                          <div className="ml-4 space-y-0.5">
                            {folder.requests.map((request) =>
                              renderRequest(collection.id, request, folder.id, collection.folders)
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Root Requests */}
                    {collection.requests
                      ?.filter((r) => !r.folderId)
                      .map((request) =>
                        renderRequest(collection.id, request, null, collection.folders)
                      )}

                    {/* Add Request Button */}
                    <button
                      className="flex items-center gap-1 px-2 py-1 text-sm text-muted-foreground hover:text-foreground w-full"
                      onClick={() => handleAddFolder(collection.id)}
                    >
                      <Plus className="w-3 h-3" />
                      Add Folder
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="h-full flex flex-col bg-card border-r border-border/50">
      {/* Sidebar Tab Switcher */}
      <div className="flex border-b border-border/50">
        <button
          onClick={() => setActiveTab('collections')}
          className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium transition-colors ${
            activeTab === 'collections'
              ? 'text-foreground border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          title="Collections"
        >
          <FolderOpen className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Collections</span>
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium transition-colors ${
            activeTab === 'history'
              ? 'text-foreground border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          title="History"
        >
          <Clock className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">History</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {activeTab === 'collections' ? <CollectionsContent /> : <HistoryList />}
      </div>

      {/* Collection Dialog */}
      <CollectionDialog
        open={collectionDialogOpen}
        onOpenChange={setCollectionDialogOpen}
        editingCollection={editingCollection}
      />

      {/* Folder Dialog */}
      <FolderDialog
        open={folderDialogOpen}
        onOpenChange={setFolderDialogOpen}
        collectionId={folderCollectionId}
        editingFolder={editingFolder}
      />

      {/* Import Collection Dialog */}
      <ImportCollectionDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
      />

      {/* Collection Settings Dialog */}
      {settingsCollection && (
        <CollectionSettingsDialog
          open={!!settingsCollection}
          onOpenChange={(open) => !open && setSettingsCollection(null)}
          collection={settingsCollection}
        />
      )}
    </div>
  );
}
